<div class="ks-container">
  <header class="ks-page-header ks-rise-1">
    <div>
      <h1 class="ks-page-title">Plantillas</h1>
      <p class="ks-page-subtitle">
        Definen la forma de una credencial: qué campos tiene, en qué orden y cuáles son
        obligatorios. Nunca guardan el contenido de la bóveda.
      </p>
    </div>

    <button type="button" class="ks-btn ks-btn--primary" (click)="create()">
      <i class="bi bi-plus-lg" aria-hidden="true"></i>
      Nueva plantilla
    </button>
  </header>

  <div class="ks-filter-bar ks-rise-2">
    <div class="ks-filter-bar__controls">
      <label class="ks-visually-hidden" for="ks-templates-search">Buscar plantilla</label>
      <input
        id="ks-templates-search"
        type="search"
        class="ks-field"
        placeholder="Buscar por nombre, código o descripción"
        [value]="search()"
        (input)="onSearchChange($any($event.target).value)" />

      <!--
        ng-select, no <select>: todos los desplegables de la consola deben
        permitir BUSCAR. El id del input interno lo pone `labelForId`, asi que el
        `<label for>` de arriba sigue apuntando al elemento que recibe el foco.
        "Todos los estados" queda como item REAL de la lista (no como
        placeholder) para no cambiar el comportamiento del select nativo que
        reemplaza.
      -->
      <div class="ks-templates__status-filter">
        <label class="ks-visually-hidden" for="ks-templates-status">Filtrar por estado</label>
        <ng-select
          labelForId="ks-templates-status"
          [items]="statusOptions"
          bindLabel="label"
          bindValue="value"
          [ngModel]="statusFilter()"
          (change)="onStatusFilterChange($event)"
          placeholder="Todos los estados"
          notFoundText="Sin resultados"
          clearAllText="Quitar selección"
          loadingText="Cargando…"
          typeToSearchText="Escribe para buscar"
          [searchable]="true"
          [clearable]="true" />
      </div>
    </div>

    <div class="ks-filter-bar__actions">
      <span class="ks-count" aria-live="polite">
        {{ visibleTemplates().length }} de {{ workingTemplates().length }} · {{ activeCount() }} activas
      </span>
    </div>
  </div>

  <section class="ks-panel ks-rise-3" aria-labelledby="ks-templates-heading">
    <div class="ks-card__header">
      <h2 class="ks-card__title" id="ks-templates-heading">Catálogo</h2>
    </div>

    <!-- Orden obligatorio: carga -> error -> vacio -> listado. El estado vacio
         solo puede aparecer cuando ya se sabe con certeza que no hay datos.
         El esqueleto debe corresponder a la vista que se va a mostrar (filas
         o tarjetas): si no, hay salto de layout al terminar de cargar.

         `showSkeleton`/`showLoadError` (no `loading()`/`error()` a secas):
         una recarga en segundo plano (tras desactivar, o "Reintentar" con
         datos ya en pantalla) NO debe volver a tapar la tabla/tarjetas ya
         pintadas ni con esqueleto ni con la tarjeta de error. -->
    @if (showSkeleton()) {
      @if (!isMobile()) {
        <app-skeleton shape="line" height="52px" [count]="6" label="Cargando plantillas" />
      } @else {
        <ul class="ks-templates__cards">
          @for (item of skeletonCards; track item) {
            <li class="ks-templates__card">
              <div class="ks-skeleton-stack">
                <app-skeleton shape="line" width="70%" height="16px" label="Cargando plantillas" />
                <app-skeleton shape="line" width="45%" height="12px" />
                <app-skeleton shape="line" width="100%" height="12px" />
              </div>
            </li>
          }
        </ul>
      }
    } @else if (showLoadError()) {
      <app-empty-state
        icon="wifi-off"
        title="No se pudieron cargar las plantillas"
        [message]="error()!">
        <button type="button" class="ks-btn ks-btn--glass ks-btn--sm" (click)="load()">
          <i class="bi bi-arrow-clockwise" aria-hidden="true"></i>
          Reintentar
        </button>
      </app-empty-state>
    } @else if (visibleTemplates().length === 0) {
      <app-empty-state
        icon="file-earmark-text"
        title="Sin plantillas"
        message="No hay plantillas que coincidan con el filtro. Crea una para empezar a definir credenciales.">
        <button type="button" class="ks-btn ks-btn--glass ks-btn--sm" (click)="create()">
          <i class="bi bi-plus-lg" aria-hidden="true"></i>
          Nueva plantilla
        </button>
      </app-empty-state>
    } @else if (!isMobile()) {
      <div class="ks-table-wrap">
        <table class="ks-table ks-table--sticky-first">
          <caption class="ks-visually-hidden">
            Plantillas del catálogo, con sus campos, indicadores y estado.
          </caption>

          <thead>
            <tr>
              <th scope="col">Plantilla</th>
              <th scope="col">Descripción</th>
              <th scope="col">Detalles</th>
              <th scope="col">Campo principal</th>
              <th scope="col">Estado</th>
              <th scope="col"><span class="ks-visually-hidden">Acciones</span></th>
            </tr>
          </thead>

          <tbody>
            @for (template of visibleTemplates(); track template.templateId) {
              @let state = rowState(template);

              <tr
                class="ks-row"
                [class.ks-row--pending]="!!state"
                [appRowLink]="state ? null : ['/dashboard/catalogues/templates', template.templateId]">
                <td>
                  <div class="ks-templates__identity">
                    <span class="ks-templates__thumb" aria-hidden="true">
                      @if (template.image) {
                        <img [src]="template.image.urlFile" alt="" loading="lazy" />
                      } @else {
                        <i class="bi bi-file-earmark-text"></i>
                      }
                    </span>

                    <div class="ks-templates__identity-body">
                      <!-- Mientras la fila esta pendiente (alta o edicion en
                           curso) el nombre NO es un enlace: en un alta el id
                           es temporal y no existe esa ruta todavia, y en una
                           edicion se evita ofrecer un camino por teclado que
                           el clic de la fila (`appRowLink`) ya bloqueo. -->
                      @if (state) {
                        <span class="ks-templates__name ks-templates__name--pending">
                          {{ template.name }}
                        </span>
                      } @else {
                        <a
                          class="ks-templates__name"
                          [routerLink]="['/dashboard/catalogues/templates', template.templateId]">
                          {{ template.name }}
                        </a>
                      }
                      <span class="ks-chip ks-chip--purple ks-mono ks-templates__code">{{ template.code }}</span>
                    </div>
                  </div>
                </td>

                <td class="ks-templates__description">
                  @if (template.description) {
                    <span [title]="template.description">{{ template.description }}</span>
                  } @else {
                    <span class="ks-muted">Sin descripción</span>
                  }
                </td>

                <td>
                  <div class="ks-flags">
                    <span class="ks-badge ks-badge--info">
                      <i class="bi bi-list-ol" aria-hidden="true"></i>
                      {{ fieldCount(template) }} campos
                    </span>

                    @if (template.generateQr) {
                      <span class="ks-badge ks-badge--info">
                        <i class="bi bi-qr-code" aria-hidden="true"></i>
                        QR
                      </span>
                    }
                  </div>
                </td>

                <td class="ks-templates__primary-field">
                  @if (primaryFieldLabel(template); as primaryLabel) {
                    <span class="ks-badge ks-badge--info" [title]="primaryLabel">
                      <i class="bi bi-star-fill" aria-hidden="true"></i>
                      <span class="ks-templates__primary-field-label">{{ primaryLabel }}</span>
                    </span>
                  } @else {
                    <span class="ks-muted">Sin campo principal</span>
                  }
                </td>

                <td>
                  @if (state) {
                    <span class="ks-sync-status">
                      <i class="bi bi-arrow-repeat ks-spin" aria-hidden="true"></i>
                      {{ rowStatusText(state.status) }}
                    </span>
                  } @else if (template.status) {
                    <span class="ks-badge ks-badge--success">Activa</span>
                  } @else {
                    <span class="ks-badge ks-badge--muted">Inactiva</span>
                  }
                </td>

                <td>
                  <div class="ks-table__actions">
                    <button
                      type="button"
                      class="ks-icon-btn"
                      [disabled]="!!state"
                      [attr.aria-label]="rowActionLabel('Editar la plantilla ' + template.name, template)"
                      (click)="edit(template)">
                      <i class="bi bi-pencil" aria-hidden="true"></i>
                    </button>

                    @if (template.status) {
                      <button
                        type="button"
                        class="ks-icon-btn ks-icon-btn--danger"
                        [disabled]="!!state"
                        [attr.aria-label]="rowActionLabel('Desactivar la plantilla ' + template.name, template)"
                        (click)="deactivate(template)">
                        <i class="bi bi-slash-circle" aria-hidden="true"></i>
                      </button>
                    }
                  </div>
                </td>
              </tr>
            }
          </tbody>
        </table>
      </div>
    } @else {
      <ul class="ks-templates__cards">
        @for (template of visibleTemplates(); track template.templateId) {
          @let state = rowState(template);

          <li
            class="ks-templates__card"
            [class.ks-templates__card--off]="!template.status && !state"
            [class.ks-templates__card--pending]="!!state"
            [appRowLink]="state ? null : ['/dashboard/catalogues/templates', template.templateId]">
            <div class="ks-templates__card-head">
              <span class="ks-templates__thumb" aria-hidden="true">
                @if (template.image) {
                  <img [src]="template.image.urlFile" alt="" loading="lazy" />
                } @else {
                  <i class="bi bi-file-earmark-text"></i>
                }
              </span>

              <div class="ks-templates__identity-body">
                @if (state) {
                  <span class="ks-templates__name ks-templates__name--pending">
                    {{ template.name }}
                  </span>
                } @else {
                  <a
                    class="ks-templates__name"
                    [routerLink]="['/dashboard/catalogues/templates', template.templateId]">
                    {{ template.name }}
                  </a>
                }
                <span class="ks-chip ks-chip--purple ks-mono ks-templates__code">{{ template.code }}</span>
              </div>

              @if (state) {
                <span class="ks-sync-status">
                  <i class="bi bi-arrow-repeat ks-spin" aria-hidden="true"></i>
                  {{ rowStatusText(state.status) }}
                </span>
              } @else if (template.status) {
                <span class="ks-badge ks-badge--success">Activa</span>
              } @else {
                <span class="ks-badge ks-badge--muted">Inactiva</span>
              }
            </div>

            @if (template.description) {
              <p class="ks-templates__card-description">{{ template.description }}</p>
            } @else {
              <p class="ks-templates__card-description ks-muted">Sin descripción</p>
            }

            <div class="ks-templates__card-footer">
              <div class="ks-flags">
                <span class="ks-badge ks-badge--info">
                  <i class="bi bi-list-ol" aria-hidden="true"></i>
                  {{ fieldCount(template) }} campos
                </span>

                @if (template.generateQr) {
                  <span class="ks-badge ks-badge--info">
                    <i class="bi bi-qr-code" aria-hidden="true"></i>
                    QR
                  </span>
                }
              </div>

              <div class="ks-table__actions">
                <button
                  type="button"
                  class="ks-icon-btn"
                  [disabled]="!!state"
                  [attr.aria-label]="rowActionLabel('Editar la plantilla ' + template.name, template)"
                  (click)="edit(template)">
                  <i class="bi bi-pencil" aria-hidden="true"></i>
                </button>

                @if (template.status) {
                  <button
                    type="button"
                    class="ks-icon-btn ks-icon-btn--danger"
                    [disabled]="!!state"
                    [attr.aria-label]="rowActionLabel('Desactivar la plantilla ' + template.name, template)"
                    (click)="deactivate(template)">
                    <i class="bi bi-slash-circle" aria-hidden="true"></i>
                  </button>
                }
              </div>
            </div>
          </li>
        }
      </ul>
    }
  </section>
</div>
